import {
  diamondSymbol,
  pentagonSymbol,
  squareSymbol,
  symbolPathToSvgString,
  upDownTriangleSymbol,
} from '@/summaryReport/SummaryReportSVGArrayFunctions';
import { RunRules } from '@/tools/statisticsSummary/statisticsSummary.constants';
import { DEFAULT_AXIS_LABEL_COLOR, DEFAULT_AXIS_LABEL_COLOR_DARK } from '@/trend/trendViewer/trendViewer.constants';

export const CHARTS_ORDER = ['I', 'MR', 'XBAR', 'R', 'S'];
export const RUN_RULES_ORDER_OF_PRIORITY = [RunRules.WE1, RunRules.WE2, RunRules.WE3, RunRules.WE4];
export const COMPARISON_HEADER = 'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.COMPARISON_TESTS';
export const COMPARISON_BASELINE_VALUE = 'INVESTIGATE_TOOLS.SUMMARY_REPORT.COMPARISON_BASELINE_VALUE';
export const COMPARISON_NO_VALUE = 'INVESTIGATE_TOOLS.SUMMARY_REPORT.NOT_APPLICABLE';
export const STATS_SUMMARY_GROUP_HEADER = 'Group Index';
export const CAPSULE_INDEX_HEADER = 'Capsule Index';

export const DARK_THEME_DARK_COLOR = '#3a3a3a';
export const DARK_THEME_WHITE_COLOR = '#FFFFFF';
export const CONTROL_LIMITS_COLOR = '#bf0000';
export const CENTER_LINE_COLOR = '#427c63';
export const WE4_VIOLATION = '#8800ff';
export const ONE_SIGMA_COLOR = '#117fbf';
export const TWO_SIGMA_COLOR = '#f26722';
export const CONTROL_LIMITS_KEYS = ['ucl', 'lcl', 'centerLine'];
export const SIGMA_LEVELS_KEYS = ['plus1Sigma', 'minus1Sigma', 'plus2Sigma', 'minus2Sigma'];
export const START_TIME = 'Start Timestamp';

export const CHART_TABLE_COL_NAME = {
  I: {
    name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.CHARTS.INDIVIDUALS',
    value: 'I Value',
  },
  MR: {
    name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.CHARTS.MOVING_RANGE',
    value: 'MR Value',
  },
  XBAR: {
    name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.CHARTS.XBAR',
    value: 'XBAR Value',
  },
  R: {
    name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.CHARTS.RANGE',
    value: 'R Value',
  },
  S: {
    name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.CHARTS.STANDARD_DEVIATION',
    value: 'S Value',
  },
};

export const CONTROL_CHARTS_MAP = {
  IMR: {
    name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.CHARTS.IMR',
    primary: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.CHARTS.INDIVIDUALS',
    secondary: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.CHARTS.MOVING_RANGE',
  },
  XBARR: {
    name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.CHARTS.XBAR_R',
    primary: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.CHARTS.XBAR',
    secondary: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.CHARTS.RANGE',
  },
  XBARS: {
    name: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.CHARTS.XBAR_S',
    primary: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.CHARTS.XBAR',
    secondary: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.CHARTS.STANDARD_DEVIATION',
  },
};

export const STATS_LIGHT_THEME = {
  title: {
    style: {
      color: undefined,
    },
  },
  xAxis: {
    title: {
      style: { color: DEFAULT_AXIS_LABEL_COLOR },
    },
  },
  yAxis: {
    title: {
      style: { color: DEFAULT_AXIS_LABEL_COLOR },
    },
  },
};
export const STATS_DARK_THEME = {
  title: {
    style: {
      color: DARK_THEME_WHITE_COLOR,
    },
  },
  xAxis: {
    title: {
      style: { color: DEFAULT_AXIS_LABEL_COLOR_DARK },
    },
  },
  yAxis: {
    title: {
      style: { color: DEFAULT_AXIS_LABEL_COLOR_DARK },
    },
  },
  tooltip: {
    backgroundColor: DARK_THEME_DARK_COLOR,
    style: {
      color: DARK_THEME_WHITE_COLOR,
    },
  },
};

export const SUMMARY_REPORT_TABLE_HEADERS = [
  'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.DESCRIPTIVE_STATISTICS',
  'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.DISTRIBUTION',
  'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.QUARTILES',
  'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.CAPABILITY_AND_PERFORMANCE',
  COMPARISON_HEADER,
];

export const CURSOR_Y_OFFSET = 15;
export const BOXPLOT_LABEL_Z_INDEX = 5;
export const SCATTER_DOT_RADIUS = 1;
export const SCATTER_DOT_OVERLAY_RADIUS = 1.5;

export const SUMMARY_REPORT_TABLE_PROPS = [
  {
    key: 'average',
    label: 'STATISTICS.AVERAGE.LONG',
    group: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.DESCRIPTIVE_STATISTICS',
  },
  {
    key: 'count',
    label: 'STATISTICS.COUNT.LONG',
    group: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.DESCRIPTIVE_STATISTICS',
  },
  {
    key: 'max',
    label: 'STATISTICS.MAXIMUM.LONG',
    group: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.DESCRIPTIVE_STATISTICS',
  },
  {
    key: 'min',
    label: 'STATISTICS.MINIMUM.LONG',
    group: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.DESCRIPTIVE_STATISTICS',
  },
  {
    key: 'stdDev',
    label: 'STATISTICS.STANDARD_DEVIATION.LONG',
    group: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.DESCRIPTIVE_STATISTICS',
  },
  {
    key: 'pValue',
    label: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.NORMAL_DISTRIBUTION_P_VALUE',
    group: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.DISTRIBUTION',
  },
  {
    key: 'minWhisker',
    label: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.MIN_WHISKER',
    group: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.QUARTILES',
  },
  {
    key: 'quartile1',
    label: 'STATISTICS.FIRST_QUARTILE.LONG',
    group: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.QUARTILES',
  },
  {
    key: 'quartile2',
    label: 'STATISTICS.MEDIAN.LONG',
    group: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.QUARTILES',
  },
  {
    key: 'quartile3',
    label: 'STATISTICS.THIRD_QUARTILE.LONG',
    group: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.QUARTILES',
  },
  {
    key: 'maxWhisker',
    label: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.MAX_WHISKER',
    group: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.QUARTILES',
  },
  {
    key: 'cpk',
    label: 'STATISTICS.PROCESS_CAPABILITY_INDEX.SHORT',
    group: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.CAPABILITY_AND_PERFORMANCE',
  },
  {
    key: 'ppk',
    label: 'STATISTICS.PROCESS_PERFORMANCE_INDEX.SHORT',
    group: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.CAPABILITY_AND_PERFORMANCE',
  },
  {
    key: 'pp',
    label: 'STATISTICS.PROCESS_PERFORMANCE.SHORT',
    group: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.CAPABILITY_AND_PERFORMANCE',
  },
  {
    key: 'ppl',
    label: 'STATISTICS.PROCESS_PERFORMANCE_LOWER.SHORT',
    group: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.CAPABILITY_AND_PERFORMANCE',
  },
  {
    key: 'ppu',
    label: 'STATISTICS.PROCESS_PERFORMANCE_UPPER.SHORT',
    group: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.CAPABILITY_AND_PERFORMANCE',
  },
];

export const COMPARISON_TABLE_PROPS = [
  {
    key: 'meanPValue',
    label: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.COMPARISON_TESTS.EQUAL_MEAN_P_VALUE',
    group: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.COMPARISON_TESTS',
  },
  {
    key: 'variancePValue',
    label: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.COMPARISON_TESTS.EQUAL_VARIANCE_P_VALUE',
    group: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.STAT_GROUP_HEADERS.COMPARISON_TESTS',
  },
];

export enum Y_AXIS_LABEL {
  COUNT = 'COUNT',
  PERCENTAGE = 'PERCENTAGE',
}

export enum X_AXIS_LABEL {
  OFF = 'OFF',
  UNIT = 'UNIT',
}

export const X_AXIS_LABEL_OPTIONS = [
  {
    id: 'labelUnits',
    value: X_AXIS_LABEL.UNIT,
    label: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.LABEL.UNIT',
  },
  {
    id: 'labelOff',
    value: X_AXIS_LABEL.OFF,
    label: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.LABEL.OFF',
  },
];

export const Y_AXIS_LABEL_OPTIONS = [
  {
    id: 'labelCount',
    value: Y_AXIS_LABEL.COUNT,
    label: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.LABEL.COUNT',
    chartLabel: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.LABEL.COUNT',
  },
  {
    id: 'labelPercentage',
    value: Y_AXIS_LABEL.PERCENTAGE,
    label: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.LABEL.PERCENTAGE',
    chartLabel: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.LABEL.PERCENTAGE_SYMBOL',
  },
];

export const SHOW_HIDE_OPTIONS = [
  {
    id: 'show',
    value: true,
    label: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.LABEL.SHOW',
  },
  {
    id: 'hide',
    value: false,
    label: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.LABEL.HIDE',
  },
];

export const BOX_PLOT_POINT_LABEL_OPTIONS = [
  {
    id: 'labelOn',
    value: true,
    label: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.LABEL.ON',
  },
  {
    id: 'labelOff',
    value: false,
    label: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.LABEL.OFF',
  },
];

export const LIMITS_DISPLAY_PROPS = {
  centerLine: {
    color: CENTER_LINE_COLOR,
    dashStyle: 'Solid',
    width: 1,
    label: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.LIMITS.CENTER_LINE',
  },
  ucl: {
    color: CONTROL_LIMITS_COLOR,
    dashStyle: 'Dash',
    width: 1,
    label: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.LIMITS.UCL',
  },
  lcl: {
    color: CONTROL_LIMITS_COLOR,
    dashStyle: 'Dash',
    width: 1,
    label: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.LIMITS.LCL',
  },
  plus1Sigma: {
    color: ONE_SIGMA_COLOR,
    dashStyle: 'ShortDot',
    width: 1,
    label: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.LIMITS.PLUS_1_SIGMA',
  },
  minus1Sigma: {
    color: ONE_SIGMA_COLOR,
    dashStyle: 'ShortDot',
    width: 1,
    label: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.LIMITS.MINUS_1_SIGMA',
  },
  plus2Sigma: {
    color: TWO_SIGMA_COLOR,
    dashStyle: 'ShortDot',
    width: 1,
    label: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.LIMITS.PLUS_2_SIGMA',
  },
  minus2Sigma: {
    color: TWO_SIGMA_COLOR,
    dashStyle: 'ShortDot',
    width: 1,
    label: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.LIMITS.MINUS_2_SIGMA',
  },
};

export const RUN_RULES_MARKER_MAP = {
  WE1: {
    enabled: true,
    fillColor: CONTROL_LIMITS_COLOR,
    radius: 2,
    symbol: 'srSquare',
    rule: 'WE1',
    icon: symbolPathToSvgString(squareSymbol),
  },
  WE2: {
    enabled: true,
    fillColor: TWO_SIGMA_COLOR,
    radius: 3,
    symbol: 'srPentagon',
    rule: 'WE2',
    icon: symbolPathToSvgString(pentagonSymbol),
  },
  WE3: {
    enabled: true,
    fillColor: ONE_SIGMA_COLOR,
    radius: 3,
    symbol: 'srUpDownTriangle',
    rule: 'WE3',
    icon: symbolPathToSvgString(upDownTriangleSymbol),
  },
  WE4: {
    enabled: true,
    fillColor: WE4_VIOLATION,
    radius: 3,
    symbol: 'srDiamond',
    rule: 'WE4',
    icon: symbolPathToSvgString(diamondSymbol),
  },
};

export const TOOLBAR_SIZE = {
  DEFAULT: 'default',
  SMALL: 'sm',
  EXTRA_SMALL: 'xs',
} as const;

export type ToolbarSize = (typeof TOOLBAR_SIZE)[keyof typeof TOOLBAR_SIZE];
